/**
 * Video inline-playback compatibility fix
 * --------------------------------------------------------------------------
 * Some in-app browsers ignore the standard `playsinline` attribute and force
 * ANY <video> on the page into their own native fullscreen player the
 * moment something calls .play() on it -- even a video that isn't part of
 * the section currently on screen (e.g. this theme's hover-preview video on
 * a product card, or this carousel's autoplay-on-scroll video), which is
 * jarring for someone just trying to scroll.
 *
 * TikTok's in-app browser in particular has been confirmed (by testing) to
 * force fullscreen on *every* scripted .play() call no matter what
 * inline-playback attributes are present, so for TikTok specifically this
 * disables scripted playback entirely, site-wide, regardless of which
 * feature (carousel, hover preview, etc.) tries to trigger it -- those
 * videos simply sit on their first frame there instead of autoplaying.
 *
 * For other in-app browsers (WeChat/QQ and similar) that respect the
 * vendor-prefixed inline attributes, this keeps videos playing normally
 * inline and adds a safety net that exits fullscreen if one is still forced.
 */
(function () {
  var isTikTokWebview = /BytedanceWebview|musical_ly|trill_/i.test(navigator.userAgent);

  var inlineAttributes = {
    playsinline: '',
    'webkit-playsinline': 'true',
    'x5-playsinline': 'true',
    'x5-video-player-type': 'h5',
    'x5-video-player-fullscreen': 'false',
  };

  function exitForcedFullscreen(video) {
    try {
      if (typeof video.webkitExitFullscreen === 'function') {
        video.webkitExitFullscreen();
      }
    } catch (error) {}

    try {
      if (document.fullscreenElement === video && typeof document.exitFullscreen === 'function') {
        document.exitFullscreen();
      }
    } catch (error) {}

    video.pause();
  }

  function disableScriptedPlayback(video) {
    video.removeAttribute('autoplay');
    video.pause();

    // Neutralize .play() itself so it doesn't matter which script (this
    // carousel, the theme's hover-preview video, anything else) calls it --
    // none of them can trigger TikTok's fullscreen takeover.
    video.play = function () {
      return Promise.resolve();
    };
  }

  function makeInline(video) {
    if (!video || video.hasAttribute('data-video-inline-fix')) return;
    video.setAttribute('data-video-inline-fix', '');

    for (var attribute in inlineAttributes) {
      if (Object.prototype.hasOwnProperty.call(inlineAttributes, attribute)) {
        video.setAttribute(attribute, inlineAttributes[attribute]);
      }
    }

    video.addEventListener('webkitbeginfullscreen', function () {
      exitForcedFullscreen(video);
    });

    video.addEventListener('x5videoenterfullscreen', function () {
      exitForcedFullscreen(video);
    });

    if (isTikTokWebview) {
      disableScriptedPlayback(video);
    }
  }

  function scanForVideos(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    root.querySelectorAll('video').forEach(makeInline);
  }

  scanForVideos(document);

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'VIDEO') makeInline(node);
        scanForVideos(node);
      });
    });
  });

  observer.observe(document.documentElement, {childList: true, subtree: true});
})();
